import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import { AdminRoutingLog } from './pages/AdminRoutingLog';
import { AdminAdvisors } from './pages/AdminAdvisors';
import { AdminAvailability } from './pages/AdminAvailability';
import { AdvisorMe } from './pages/AdvisorMe';
import { RescheduleBooking } from './pages/RescheduleBooking';
import { Templates } from './pages/Templates';
import { AdminReminderOptOuts } from './pages/AdminReminderOptOuts';
import { AdminJourneyQc } from './pages/AdminJourneyQc';
import { AdminJourneyAudit } from './pages/AdminJourneyAudit';
import { AdminJourneyProspects } from './pages/AdminJourneyProspects';
import { AdminHome } from './pages/AdminHome';

// PR #30 — code-split the personal scheduler page. It's only loaded for
// /<slug> URLs, so the main booking flow doesn't pay the bundle cost.
const PersonalScheduler = lazy(() =>
  import('./pages/PersonalScheduler').then((m) => ({ default: m.PersonalScheduler }))
);

// Decision Center v0 (RDJ) — lazy-loaded; only /dc/<token> visitors pay
// for the bundle, mirroring the PersonalScheduler pattern above.
const DecisionCenter = lazy(() =>
  import('./pages/DecisionCenter').then((m) => ({ default: m.DecisionCenter }))
);

// C1 (RDJ) — magic-link landing for prospects who lost their portal email.
// Bare /dc (no token) renders the request form; /dc/<token> stays below.
const DcRequestLink = lazy(() =>
  import('./pages/DcRequestLink').then((m) => ({ default: m.DcRequestLink }))
);

// Reserved top-level paths that take priority over a personal /<slug>
// match. Mirrors the trigger function in migration 0012, but kept in
// sync manually rather than fetched at runtime — these paths change
// rarely and the duplication is cheaper than a round-trip on every
// page load. Migration 0012 is the enforcement boundary; this list is
// the routing fast-path.
const RESERVED_TOP_LEVEL = new Set([
  'admin',
  'api',
  'dc',
  'r',
  'me',
  'templates',
  'unsubscribe',
  'dashboard',
  'login',
  'logout',
  'health',
  'healthz',
  'assets',
  '_next',
  'static',
  'public',
  'favicon.ico',
  'robots.txt',
  'sitemap.xml',
]);

// Personal slug shape — mirrors migration 0012's CHECK constraint.
// Lowercase letters/digits with internal dot/hyphen, 3–30 chars total.
const PERSONAL_SLUG_RE = /^[a-z0-9][a-z0-9.\-]{1,28}[a-z0-9]$/;

// PR #30.5 — meeting-type slug shape (per /api/templates/meeting-types
// rows; mirrors migration 0010 seed slugs). Same constraint shape as
// the advisor slug, conveniently. Matched against the second segment
// of two-segment personal-link paths like /tom/2nd-meeting.
const MEETING_TYPE_SLUG_RE = /^[a-z0-9][a-z0-9.\-]{1,28}[a-z0-9]$/;

/**
 * Detect a personal-link path. Two shapes are accepted:
 *   /<slug>                       → 1st-meeting (default)
 *   /<slug>/<meeting-type-slug>   → that specific meeting type
 *
 * In both cases the first segment must NOT be in the reserved list,
 * and both segments must match the slug shape. Server-side validation
 * (the actual DB lookup) is the source of truth for whether the
 * advisor / meeting type exists; the regex here is just the fast-path
 * filter to avoid loading the personal scheduler bundle on a 404.
 */
function looksLikePersonalSlug(path: string): boolean {
  const trimmed = path.replace(/^\/+/, '').replace(/\/+$/, '');
  if (!trimmed) return false;
  const segments = trimmed.split('/');
  if (segments.length === 0 || segments.length > 2) return false;
  const [first, second] = segments;
  if (RESERVED_TOP_LEVEL.has(first)) return false;
  if (!PERSONAL_SLUG_RE.test(first)) return false;
  if (segments.length === 2 && !MEETING_TYPE_SLUG_RE.test(second)) return false;
  return true;
}

// V1: lightweight pathname routing. We only have a few static routes
// (booking flow + reschedule + admin + dashboard mockup), so a full
// router is overkill. V2 task #28 adds Supabase Auth + multiple
// advisor dashboards — at that point we'll bring in react-router-dom.
function Root() {
  const path = window.location.pathname;
  // Admin home — every admin surface in one place.
  if (path === '/admin' || path === '/admin/') {
    return <AdminHome />;
  }
  if (path.startsWith('/admin/routing')) {
    return <AdminRoutingLog />;
  }
  if (path.startsWith('/admin/reminder-opt-outs')) {
    return <AdminReminderOptOuts />;
  }
  // RDJ QC — prospect console: one CRM-style profile per enrolled prospect. Must precede /admin/journey.
  if (path.startsWith('/admin/journey/prospects')) {
    return <AdminJourneyProspects />;
  }
  // RDJ QC — random prospect audits (replaces the canary). Must precede /admin/journey.
  if (path.startsWith('/admin/journey/audit')) {
    return <AdminJourneyAudit />;
  }
  // RDJ Workstream E — the journey QC / transparency console
  if (path.startsWith('/admin/journey')) {
    return <AdminJourneyQc />;
  }
  if (path.startsWith('/admin/availability')) {
    return <AdminAvailability />;
  }
  if (path.startsWith('/admin/advisors')) {
    return <AdminAdvisors />;
  }
  if (path === '/me' || path.startsWith('/me/')) {
    return <AdvisorMe />;
  }
  if (path === '/templates' || path.startsWith('/templates/')) {
    return <Templates />;
  }
  if (path.startsWith('/r/')) {
    return <RescheduleBooking />;
  }
  // C1 (RDJ) — bare /dc: the magic-link request form. Must come before
  // the /dc/<token> match below (startsWith would never see bare /dc, but
  // keeping them adjacent makes the pairing obvious).
  if (path === '/dc' || path === '/dc/') {
    return (
      <Suspense
        fallback={
          <div style={{ padding: 48, textAlign: 'center', color: '#8C8C8C' }}>
            Loading…
          </div>
        }
      >
        <DcRequestLink />
      </Suspense>
    );
  }
  // Decision Center v0 (RDJ) — token-gated prospect portal. Must stay in
  // the reserved-prefix section above the personal-slug fallthrough.
  if (path.startsWith('/dc/')) {
    return (
      <Suspense
        fallback={
          <div style={{ padding: 48, textAlign: 'center', color: '#8C8C8C' }}>
            Loading…
          </div>
        }
      >
        <DecisionCenter />
      </Suspense>
    );
  }
  // PR #30 — personal advisor link (e.g. /jeremiah). Must come AFTER
  // all reserved-prefix matches above so they win when there's any
  // ambiguity. Lazy-loaded, with a quiet loading fallback so the
  // initial paint doesn't show a flash of "loading…" for cached
  // bundles.
  if (looksLikePersonalSlug(path)) {
    return (
      <Suspense
        fallback={
          <div style={{ padding: 48, textAlign: 'center', color: '#8C8C8C' }}>
            Loading…
          </div>
        }
      >
        <PersonalScheduler />
      </Suspense>
    );
  }
  return <App />;
}

// Sentry — frontend error tracking. Safe to embed the DSN; it's a public
// ingest key, not a secret. Init is no-op when env var isn't present so
// local dev / preview deployments without the var don't fail.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    // V1: errors only — no perf, no replays. We can dial these up later.
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Filter out noise from extensions / known browser quirks
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={
        <div style={{ padding: 32, textAlign: 'center' }}>
          <h2>Something went wrong.</h2>
          <p>
            We've been notified. Please refresh the page or email{' '}
            <a href="mailto:scheduler@annuityassociation.com">
              scheduler@annuityassociation.com
            </a>{' '}
            if this keeps happening.
          </p>
        </div>
      }
    >
      <Root />
    </Sentry.ErrorBoundary>
  </StrictMode>
);
